// tournamentUI.ts - Tournament banner, registration, code entry, and faction draft UI extracted from main.js

import { ThemeManager } from './theme.js';
import { gameState, getFactions, Board } from './game.ts';
import { BugReport } from './bug-report.ts';
import { StandardRemoteDriver } from './drivers/standard.ts';
import { playerProfile } from './playerProfile.ts';
import { openModal, closeModal, showError, hideError, setButtonLoading } from './uiUtils.ts';
import type { ErrorMessage } from '../../shared/protocol.ts';
import type { AbilityManager } from './abilities.ts';
import type { NetworkManager } from './network.ts';
import type { renderer as rendererInstance } from './renderer.ts';

type Renderer = typeof rendererInstance;

/** Lazily-loaded tactical module (main.ts hands us its loader). */
type TacticalModule = typeof import('./tactical/main.ts');

/** The slice of main.ts's shared context blob that tournamentUI reads. */
export interface TournamentUIContext {
    renderer: Renderer;
    abilityManager: AbilityManager;
    networkManager: NetworkManager;
    getDriver: () => StandardRemoteDriver | null;
    setDriver: (d: StandardRemoteDriver) => void;
    setupPlacementHandlers: () => void;
    loadTacticalModule: () => Promise<TacticalModule>;
}

// ─── REST payload types ──────────────────────────────────────────
// Local interfaces mirroring the server emits (source of truth:
// server/tournamentRoutes.mts and tournamentManager.mts).

/**
 * GET /api/tournaments/active `tournament` payload —
 * tournamentManager.getActiveTournamentForBanner() (TournamentRow + banner
 * extras; only the client-read fields are declared).
 */
interface ActiveTournamentBanner {
    id: number;
    name: string;
    /** 'registration' | 'active'. */
    status: string;
    player_count: number;
    /** 'standard' | 'tactical' (nullable on legacy rows). */
    game_mode?: string | null;
    /** Present for active tournaments (last completed round + 1). */
    displayRound?: number;
    [extra: string]: unknown;
}

/** GET /api/tournaments/active response. */
interface ActiveTournamentResponse {
    tournament: ActiveTournamentBanner | null;
    error?: string;
}

/** POST /api/tournaments/:id/register response: success fields on 200, { error } otherwise. */
interface TournamentRegisterResponse {
    success?: boolean;
    registrationCode?: string;
    playerId?: number;
    error?: string;
}

/** POST /api/tournaments/validate-code response (player details unused here). */
interface ValidateCodeResponse {
    valid?: boolean;
    error?: string;
}

// ─── Draft socket payloads ───────────────────────────────────────
// Emitted by server/handlers/draftHandlers.mts (not yet in shared/protocol.ts).

/** `draft-round2` — the combined nomination pool. */
interface DraftRound2 {
    pool: string[];
}

/** `draft-result` — final faction and how it was decided ('agreed' | 'random'). */
interface DraftResult {
    faction: string;
    method: string;
}

let ctx: TournamentUIContext;

// Tournament banner state
let activeTournament: ActiveTournamentBanner | null = null;

/** Faction draft state — phase: 'waiting' | 'nominating' | 'picking' | 'complete'. */
interface DraftState {
    phase: string;
    selectedFactions: string[];
    maxNominations: number;
    pool: string[];
    result: string | null;
}

// Faction draft state
let draftState: DraftState = {
    phase: 'waiting', // 'waiting' | 'nominating' | 'picking' | 'complete'
    selectedFactions: [],
    maxNominations: 3,
    pool: [],
    result: null
};

export function setup(context: TournamentUIContext): void {
    ctx = context;
}

// =====================
// Tournament Banner
// =====================

async function checkTournamentBanner(): Promise<void> {
    try {
        const response = await fetch('/djbship/api/tournaments/active');
        const data = await response.json() as ActiveTournamentResponse;

        if (data.tournament) {
            activeTournament = data.tournament;
            showTournamentBanner(data.tournament);
        } else {
            hideTournamentBanner();
        }
    } catch (err) {
        console.error('Failed to check tournament banner:', err);
        hideTournamentBanner();
    }
}

function showTournamentBanner(tournament: ActiveTournamentBanner): void {
    const banner = document.getElementById('tournament-banner');
    const badge = document.getElementById('tournament-badge')!;
    const nameEl = document.getElementById('tournament-banner-name')!;
    const playersEl = document.getElementById('tournament-banner-players')!;
    const roundEl = document.getElementById('tournament-banner-round')!;
    const registerBtn = document.getElementById('btn-tournament-register');

    if (!banner) return;

    nameEl.textContent = tournament.name;
    playersEl.textContent = `${tournament.player_count || 0} Players`;

    // Show game mode indicator
    const modeEl = document.getElementById('tournament-banner-mode');
    if (modeEl) {
        const mode = tournament.game_mode || 'standard';
        modeEl.textContent = mode === 'tactical' ? 'TACTICAL' : 'STANDARD';
        modeEl.className = 'tournament-mode-badge tournament-mode-' + mode;
    }

    // Update badge and register button based on status
    if (tournament.status === 'registration') {
        badge.textContent = 'REGISTRATION OPEN';
        badge.classList.add('registration');
        roundEl.textContent = 'Sign up now!';
        roundEl.classList.remove('hidden');
        if (registerBtn) registerBtn.classList.remove('hidden');
    } else if (tournament.displayRound) {
        badge.textContent = 'LIVE TOURNAMENT';
        badge.classList.remove('registration');
        roundEl.textContent = `Round ${tournament.displayRound}`;
        roundEl.classList.remove('hidden');
        if (registerBtn) registerBtn.classList.add('hidden');
    } else {
        badge.textContent = 'TOURNAMENT';
        badge.classList.remove('registration');
        roundEl.textContent = '';
        if (registerBtn) registerBtn.classList.add('hidden');
    }

    banner.classList.remove('hidden');
}

function hideTournamentBanner(): void {
    const banner = document.getElementById('tournament-banner');
    if (banner) {
        banner.classList.add('hidden');
    }
    activeTournament = null;
}

export function setupTournamentBanner(): void {
    const codeBtn = document.getElementById('btn-tournament-code');
    const bracketBtn = document.getElementById('btn-tournament-bracket');
    const modal = document.getElementById('tournament-modal');
    const closeBtn = document.getElementById('btn-close-tournament-modal');
    const backdrop = modal ? modal.querySelector('.modal-backdrop') : null;
    const submitBtn = document.getElementById('btn-submit-tournament-code');
    const codeInput = document.getElementById('tournament-code-input') as HTMLInputElement | null;
    const errorEl = document.getElementById('tournament-code-error');

    // Open modal on "Enter Code" click
    if (codeBtn) {
        codeBtn.addEventListener('click', () => {
            openTournamentModal();
        });
    }

    // Open bracket view
    if (bracketBtn) {
        bracketBtn.addEventListener('click', () => {
            if (activeTournament) {
                window.location.href = `/djbship/tournament?view=bracket&id=${activeTournament.id}`;
            }
        });
    }

    // Close modal handlers
    if (closeBtn) {
        closeBtn.addEventListener('click', closeTournamentModal);
    }
    if (backdrop) {
        backdrop.addEventListener('click', closeTournamentModal);
    }

    // Submit code
    if (submitBtn) {
        submitBtn.addEventListener('click', validateTournamentCode);
    }

    // Allow Enter key to submit
    if (codeInput) {
        codeInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                validateTournamentCode();
            }
        });

        // Auto-uppercase input
        codeInput.addEventListener('input', () => {
            codeInput.value = codeInput.value.toUpperCase();
            if (errorEl) errorEl.classList.add('hidden');
        });
    }

    // Check for tournament on page load
    checkTournamentBanner();

    // Registration modal handlers
    setupRegistrationModal();
}

// =====================
// Registration Modal
// =====================

function setupRegistrationModal(): void {
    const registerBtn = document.getElementById('btn-tournament-register');
    const modal = document.getElementById('tournament-register-modal');
    const closeBtn = document.getElementById('btn-close-register-modal');
    const backdrop = modal ? modal.querySelector('.modal-backdrop') : null;
    const submitBtn = document.getElementById('btn-submit-register');
    const nameInput = document.getElementById('register-player-name') as HTMLInputElement | null;
    const copyBtn = document.getElementById('btn-copy-reg-code');
    const goToHubBtn = document.getElementById('btn-go-to-hub');

    if (registerBtn) {
        registerBtn.addEventListener('click', openRegistrationModal);
    }

    if (closeBtn) {
        closeBtn.addEventListener('click', closeRegistrationModal);
    }

    if (backdrop) {
        backdrop.addEventListener('click', closeRegistrationModal);
    }

    if (submitBtn) {
        submitBtn.addEventListener('click', submitRegistration);
    }

    if (nameInput) {
        nameInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                submitRegistration();
            }
        });
    }

    if (copyBtn) {
        copyBtn.addEventListener('click', () => {
            const code = document.getElementById('registration-code-value')!.textContent;
            navigator.clipboard.writeText(code!).then(() => {
                copyBtn.textContent = 'Copied!';
                setTimeout(() => { copyBtn.textContent = 'Copy'; }, 2000);
            });
        });
    }

    if (goToHubBtn) {
        goToHubBtn.addEventListener('click', () => {
            const code = document.getElementById('registration-code-value')!.textContent;
            window.location.href = `/djbship/tournament?code=${encodeURIComponent(code!)}`;
        });
    }
}

function openRegistrationModal(): void {
    const modal = document.getElementById('tournament-register-modal');
    const nameInput = document.getElementById('register-player-name') as HTMLInputElement | null;
    const errorEl = document.getElementById('register-error');
    const successEl = document.getElementById('register-success');
    const formEl = modal ? modal.querySelector('.modal-form') : null;
    const actionsEl = modal ? modal.querySelector('.modal-actions') : null;
    const tournamentNameEl = document.getElementById('register-tournament-name');

    if (modal) {
        // Reset to form state
        if (formEl) formEl.classList.remove('hidden');
        if (actionsEl) actionsEl.classList.remove('hidden');
        if (successEl) successEl.classList.add('hidden');
        hideError(errorEl);
        if (nameInput) nameInput.value = playerProfile.callsign || '';
        if (tournamentNameEl && activeTournament) {
            tournamentNameEl.textContent = `Join "${activeTournament.name}"`;
        }
        openModal(modal, nameInput);
    }
}

function closeRegistrationModal(): void {
    closeModal(document.getElementById('tournament-register-modal'));
}

async function submitRegistration(): Promise<void> {
    const nameInput = document.getElementById('register-player-name') as HTMLInputElement | null;
    const submitBtn = document.getElementById('btn-submit-register') as HTMLButtonElement | null;

    if (!nameInput || !activeTournament) return;

    const playerName = nameInput.value.trim();

    if (!playerName) {
        showRegistrationError('Please enter your name');
        return;
    }

    if (playerName.length < 2 || playerName.length > 50) {
        showRegistrationError('Name must be 2-50 characters');
        return;
    }

    const restore = setButtonLoading(submitBtn, 'Registering...');

    try {
        const response = await fetch(`/djbship/api/tournaments/${activeTournament.id}/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ playerName, playerId: playerProfile.id || undefined })
        });

        const data = await response.json() as TournamentRegisterResponse;

        if (!response.ok) {
            throw new Error(data.error || 'Registration failed');
        }

        showRegistrationSuccess(data.registrationCode!);
        checkTournamentBanner();
    } catch (err) {
        showRegistrationError((err as Error).message);
    } finally {
        restore();
    }
}

function showRegistrationError(message: string): void {
    showError(document.getElementById('register-error'), message);
}

function showRegistrationSuccess(code: string): void {
    const modal = document.getElementById('tournament-register-modal');
    const formEl = modal ? modal.querySelector('.modal-form') : null;
    const actionsEl = modal ? modal.querySelector('.modal-actions') : null;
    const successEl = document.getElementById('register-success');
    const codeEl = document.getElementById('registration-code-value');

    if (formEl) formEl.classList.add('hidden');
    if (actionsEl) actionsEl.classList.add('hidden');
    if (successEl) successEl.classList.remove('hidden');
    if (codeEl) codeEl.textContent = code;
}

// =====================
// Tournament Code Entry
// =====================

function openTournamentModal(): void {
    const modal = document.getElementById('tournament-modal');
    const codeInput = document.getElementById('tournament-code-input') as HTMLInputElement | null;

    if (codeInput) codeInput.value = '';
    hideError(document.getElementById('tournament-code-error'));
    openModal(modal, codeInput);
}

function closeTournamentModal(): void {
    closeModal(document.getElementById('tournament-modal'));
}

async function validateTournamentCode(): Promise<void> {
    const codeInput = document.getElementById('tournament-code-input') as HTMLInputElement | null;
    const submitBtn = document.getElementById('btn-submit-tournament-code') as HTMLButtonElement | null;

    if (!codeInput) return;

    const code = codeInput.value.trim().toUpperCase();

    if (!code || code.length < 8 || code.length > 16) {
        showTournamentCodeError('Please enter your registration code');
        return;
    }

    const restore = setButtonLoading(submitBtn, 'Validating...');

    try {
        const response = await fetch('/djbship/api/tournaments/validate-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code })
        });

        const data = await response.json() as ValidateCodeResponse;

        if (data.valid) {
            window.location.href = `/djbship/tournament?code=${encodeURIComponent(code)}`;
        } else {
            showTournamentCodeError(data.error || 'Invalid code');
        }
    } catch (err) {
        console.error('Failed to validate code:', err);
        showTournamentCodeError('Failed to validate code. Please try again.');
    } finally {
        restore();
    }
}

function showTournamentCodeError(message: string): void {
    showError(document.getElementById('tournament-code-error'), message);
}

// =====================
// Faction Draft System
// =====================

export function initDraftScreen(phase: string): void {
    draftState = {
        phase: phase || 'waiting',
        selectedFactions: [],
        maxNominations: 3,
        pool: [],
        result: null
    };

    const titleEl = document.getElementById('draft-title');
    const subtitleEl = document.getElementById('draft-subtitle');
    const statusEl = document.getElementById('draft-status');
    const actionsEl = document.getElementById('draft-actions');
    const resultEl = document.getElementById('draft-result');

    if (resultEl) resultEl.classList.add('hidden');
    if (actionsEl) actionsEl.style.display = 'none';

    if (phase === 'waiting') {
        if (titleEl) titleEl.textContent = 'FACTION DRAFT';
        if (subtitleEl) subtitleEl.textContent = 'Connecting to match...';
        if (statusEl) statusEl.textContent = 'Waiting for opponent to join';
        renderDraftFactions([]);
    } else if (phase === 'nominating') {
        startNominationPhase();
    }
}

function startNominationPhase(): void {
    draftState.phase = 'nominating';
    draftState.selectedFactions = [];

    const titleEl = document.getElementById('draft-title');
    const subtitleEl = document.getElementById('draft-subtitle');
    const statusEl = document.getElementById('draft-status');
    const actionsEl = document.getElementById('draft-actions');
    const countEl = document.getElementById('draft-count');
    const submitBtn = document.getElementById('btn-draft-submit') as HTMLButtonElement | null;

    if (titleEl) titleEl.textContent = 'FACTION DRAFT - ROUND 1';
    if (subtitleEl) subtitleEl.textContent = 'Select 3 factions you\'d like to play';
    if (statusEl) statusEl.textContent = '';
    if (actionsEl) actionsEl.style.display = '';
    if (countEl) countEl.textContent = '0 / 3 selected';
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Submit Nominations';
    }

    // Render all available factions
    const factions = getFactions();
    const availableIds = Object.keys(factions).filter(id => {
        const f = factions[id];
        return f.active !== false && !f.roguelikeOnly;
    });
    renderDraftFactions(availableIds);
}

export function startPickingPhase(pool: string[]): void {
    draftState.phase = 'picking';
    draftState.pool = pool;
    draftState.selectedFactions = [];

    const titleEl = document.getElementById('draft-title');
    const subtitleEl = document.getElementById('draft-subtitle');
    const statusEl = document.getElementById('draft-status');
    const actionsEl = document.getElementById('draft-actions');
    const countEl = document.getElementById('draft-count');
    const submitBtn = document.getElementById('btn-draft-submit') as HTMLButtonElement | null;

    if (titleEl) titleEl.textContent = 'FACTION DRAFT - ROUND 2';
    if (subtitleEl) subtitleEl.textContent = 'Pick 1 faction from the draft pool';
    if (statusEl) statusEl.textContent = '';
    if (actionsEl) actionsEl.style.display = '';
    if (countEl) countEl.textContent = '0 / 1 selected';
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Lock In';
    }

    renderDraftFactions(pool);
}

function renderDraftFactions(factionIds: string[]): void {
    const container = document.getElementById('draft-factions');
    if (!container) return;
    container.textContent = '';

    const factions = getFactions();

    for (const id of factionIds) {
        const f = factions[id];
        if (!f) continue;

        const card = document.createElement('div');
        card.className = 'draft-faction-card';
        card.dataset.factionId = id;

        // Emblem
        const emblem = document.createElement('div');
        emblem.className = 'draft-faction-emblem';
        if (f.colors) {
            emblem.style.background = f.colors.primary || '#555';
        }
        if (f.logo && (f.logo.startsWith('/') || f.logo.startsWith('http://') || f.logo.startsWith('https://'))) {
            const img = document.createElement('img');
            img.src = f.logo;
            img.alt = f.name;
            img.style.width = '30px';
            img.style.height = '30px';
            emblem.textContent = '';
            emblem.appendChild(img);
        }

        // Name
        const name = document.createElement('div');
        name.className = 'draft-faction-name';
        name.textContent = f.name;

        card.appendChild(emblem);
        card.appendChild(name);

        card.addEventListener('click', () => handleDraftCardClick(id, card));

        container.appendChild(card);
    }
}

function handleDraftCardClick(factionId: string, card: HTMLElement): void {
    if (draftState.phase === 'nominating') {
        const maxSelections = draftState.maxNominations;
        const idx = draftState.selectedFactions.indexOf(factionId);
        if (idx >= 0) {
            // Deselect
            draftState.selectedFactions.splice(idx, 1);
            card.classList.remove('selected');
        } else if (draftState.selectedFactions.length < maxSelections) {
            // Select
            draftState.selectedFactions.push(factionId);
            card.classList.add('selected');
        }

        // Update count display
        const countEl = document.getElementById('draft-count');
        if (countEl) countEl.textContent = `${draftState.selectedFactions.length} / ${maxSelections} selected`;

        // Enable submit when exactly 3 selected
        const submitBtn = document.getElementById('btn-draft-submit') as HTMLButtonElement | null;
        if (submitBtn) submitBtn.disabled = draftState.selectedFactions.length !== maxSelections;

    } else if (draftState.phase === 'picking') {
        // Single selection mode
        // Deselect all others
        document.querySelectorAll('.draft-faction-card.selected').forEach(c => c.classList.remove('selected'));
        draftState.selectedFactions = [factionId];
        card.classList.add('selected');

        const countEl = document.getElementById('draft-count');
        if (countEl) countEl.textContent = '1 / 1 selected';

        const submitBtn = document.getElementById('btn-draft-submit') as HTMLButtonElement | null;
        if (submitBtn) submitBtn.disabled = false;
    }
}

function showDraftResult(factionId: string, method: string): void {
    draftState.phase = 'complete';
    draftState.result = factionId;

    const { renderer } = ctx;
    const factions = getFactions();
    const f = factions[factionId];

    const titleEl = document.getElementById('draft-title');
    const subtitleEl = document.getElementById('draft-subtitle');
    const actionsEl = document.getElementById('draft-actions');
    const facContainer = document.getElementById('draft-factions');
    const resultEl = document.getElementById('draft-result');
    const resultFactionEl = document.getElementById('draft-result-faction');
    const resultMethodEl = document.getElementById('draft-result-method');

    if (titleEl) titleEl.textContent = 'DRAFT COMPLETE';
    if (subtitleEl) subtitleEl.textContent = '';
    if (actionsEl) actionsEl.style.display = 'none';
    if (facContainer) facContainer.style.display = 'none';

    if (resultEl) resultEl.classList.remove('hidden');
    if (resultFactionEl) resultFactionEl.textContent = f ? f.name : factionId;
    if (resultMethodEl) {
        resultMethodEl.textContent = method === 'agreed'
            ? 'Both commanders agreed!'
            : 'The Force has decided!';
    }

    // Set faction style
    if (f?.colors) {
        renderer.setFactionStyle(factionId, f.colors);
    }

    // Auto-proceed after a short delay
    setTimeout(() => {
        completeDraftAndStartGame(factionId);
    }, 2500);
}

async function completeDraftAndStartGame(factionId: string): Promise<void> {
    const { renderer, abilityManager, networkManager, getDriver, setDriver, setupPlacementHandlers } = ctx;

    try {
        const factions = getFactions();
        const factionData = factions[factionId];
        gameState.initializeShips(factionId);

        // Set faction style
        if (factionData?.colors) {
            renderer.setFactionStyle(factionId, factionData.colors);
            renderer.setFactionColors(factionData.colors);
        }

        // Set faction in theme manager for CSS variables
        ThemeManager.setFaction(factionId);

        // Initialize abilities for the drafted faction
        if (factionData?.abilityData) {
            abilityManager.initialize(factionData.abilityData);
        } else {
            abilityManager.reset();
        }

        // Create remote driver for MP battle actions
        const driver = getDriver();
        if (driver) driver.dispose();
        setDriver(new StandardRemoteDriver(networkManager, gameState.roomCode!));

        // Determine if this is a tactical tournament
        const isTactical = gameState.tournamentGameMode === 'tactical';

        if (isTactical) {
            gameState.isTactical = true;
            const { initTactical } = await ctx.loadTacticalModule();
            await initTactical({
                faction: factionId,
                mode: 'multiplayer',
                socket: networkManager,
                mainRenderer: renderer,
                roomCode: gameState.roomCode
            });
        } else {
            // Standard mode - go to placement
            gameState.playerBoard = new Board();
            renderer.showScreen('placement-screen');
            BugReport.setCurrentScreen('placement');
            renderer.renderPlacementBoard(gameState.playerBoard);
            renderer.updateShipList(gameState.ships);
            renderer.updateReadyButton(false);
            setupPlacementHandlers();
        }
    } catch (err) {
        console.error('Error completing draft:', err);
        renderer.showToast('Error starting game after draft', true);
        // Restore draft faction grid visibility so user can see the state
        const facContainer = document.getElementById('draft-factions');
        if (facContainer) facContainer.style.display = '';
    }
}

export function setupDraftSocketHandlers(): void {
    const { networkManager, renderer } = ctx;

    // Draft start - server signals both players are in, draft begins
    networkManager.on('draft-start', () => {
        renderer.showScreen('draft-screen');
        startNominationPhase();
        BugReport.setCurrentScreen('draft');
    });

    // Our nomination was acknowledged
    networkManager.on('draft-nominated', () => {
        const statusEl = document.getElementById('draft-status');
        const actionsEl = document.getElementById('draft-actions');
        if (statusEl) statusEl.textContent = 'Nominations submitted! Waiting for opponent...';
        if (actionsEl) actionsEl.style.display = 'none';

        // Disable all faction cards
        document.querySelectorAll('.draft-faction-card').forEach(c => c.classList.add('disabled'));
    });

    // Opponent has submitted their nominations
    networkManager.on('draft-opponent-nominated', () => {
        const statusEl = document.getElementById('draft-status');
        if (statusEl && draftState.phase === 'nominating') {
            statusEl.textContent = 'Opponent has submitted nominations';
        }
    });

    // Round 2 - pool available
    networkManager.on('draft-round2', (rawData?: unknown) => {
        const data = rawData as DraftRound2;
        startPickingPhase(data.pool);
    });

    // Our pick was acknowledged
    networkManager.on('draft-picked', () => {
        const statusEl = document.getElementById('draft-status');
        const actionsEl = document.getElementById('draft-actions');
        if (statusEl) statusEl.textContent = 'Pick submitted! Waiting for opponent...';
        if (actionsEl) actionsEl.style.display = 'none';

        // Disable all faction cards
        document.querySelectorAll('.draft-faction-card').forEach(c => c.classList.add('disabled'));
    });

    // Opponent has submitted their pick
    networkManager.on('draft-opponent-picked', () => {
        const statusEl = document.getElementById('draft-status');
        if (statusEl && draftState.phase === 'picking') {
            statusEl.textContent = 'Opponent has locked in their pick';
        }
    });

    // Draft result
    networkManager.on('draft-result', (rawData?: unknown) => {
        const data = rawData as DraftResult;
        showDraftResult(data.faction, data.method);
    });

    // Draft error
    networkManager.on('draft-error', (rawData?: unknown) => {
        const data = rawData as ErrorMessage;
        renderer.showToast(data.message, true);
    });
}

export function setupDraftSubmitHandler(): void {
    const { networkManager } = ctx;
    const submitBtn = document.getElementById('btn-draft-submit');
    if (!submitBtn) return;

    submitBtn.addEventListener('click', () => {
        if (draftState.phase === 'nominating' && draftState.selectedFactions.length === 3) {
            networkManager.send('draft-nominate', {
                roomCode: gameState.roomCode,
                factions: draftState.selectedFactions
            });
        } else if (draftState.phase === 'picking' && draftState.selectedFactions.length === 1) {
            networkManager.send('draft-pick', {
                roomCode: gameState.roomCode,
                faction: draftState.selectedFactions[0]
            });
        }
    });
}
